import React, { useState, useEffect } from 'react';
import { X, Shield, User } from 'lucide-react';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { getMasterPublicBaseUrl } from '@easyflow/shared';
import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';

interface ImpersonationInfo {
  originalUser: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  impersonatedAt: string;
}

export default function ImpersonationBanner() {
  const [impersonationInfo, setImpersonationInfo] = useState<ImpersonationInfo | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const impersonationToken = localStorage.getItem(AUTH_STORAGE_KEYS.IMPERSONATION_TOKEN);
    const isCurrentlyImpersonating = !!impersonationToken;
    setIsImpersonating(isCurrentlyImpersonating);

    if (isCurrentlyImpersonating) {
      const info = localStorage.getItem(AUTH_STORAGE_KEYS.IMPERSONATION_INFO);
      if (info) {
        try {
          setImpersonationInfo(JSON.parse(info));
        } catch (error) {
          console.error('[ImpersonationBanner] Failed to parse impersonation info:', error);
        }
      }
    }
  }, []);

  const handleExitImpersonation = async () => {
    console.log('[ImpersonationBanner] Exiting impersonation...');
    
    const masterBackupToken = localStorage.getItem(AUTH_STORAGE_KEYS.MASTER_BACKUP_TOKEN);
    console.log('[ImpersonationBanner] Master backup token exists:', !!masterBackupToken);
    
    try {
      // Endpoint cookie-based (`credentials: 'include'` è il default del client).
      await apiClient.post('/bff/exit-impersonation', {}, { headers: { 'X-Auth-Scope': 'tenant' } });
    } catch (err) {
      console.error('[ImpersonationBanner] Failed to call exit-impersonation endpoint:', err);
    }
    
    document.cookie = 'session_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
    localStorage.removeItem(AUTH_STORAGE_KEYS.TENANT_SLUG);
    localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATION_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATION_INFO);
    localStorage.removeItem(AUTH_STORAGE_KEYS.MASTER_BACKUP_TOKEN);
    
    
    apiClient.clearToken();
    
    setIsImpersonating(false);
    setImpersonationInfo(null);
    
    // Task #3260/#3585 — ritorno al MASTER: origin master dedicato condiviso
    // (MASTER_PUBLIC_BASE_URL, default master.<brand>), non esiste per-prodotto.
    if (masterBackupToken) {
      console.log('[ImpersonationBanner] Restoring master session...');
      localStorage.setItem('edgvoip_master_token', masterBackupToken);
      window.location.href = `${getMasterPublicBaseUrl()}/users`;
    } else {
      console.log('[ImpersonationBanner] No backup token, redirecting to master login...');
      window.location.href = `${getMasterPublicBaseUrl()}/login`;
    }
  };

  if (!isImpersonating || !impersonationInfo) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <Alert className="border-orange-200 bg-orange-50 text-orange-800 rounded-none">
        <Shield className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4" />
              <span className="font-medium">
                Impersonating: {impersonationInfo.originalUser.firstName} {impersonationInfo.originalUser.lastName}
              </span>
              <span className="text-sm text-orange-600">
                ({impersonationInfo.originalUser.email})
              </span>
            </div>
            <div className="text-sm text-orange-600">
              Started: {new Date(impersonationInfo.impersonatedAt).toLocaleTimeString()}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExitImpersonation}
            className="border-orange-300 text-orange-800 hover:bg-orange-100"
          >
            <X className="h-4 w-4 mr-2" />
            Exit Impersonation
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
